import { provide, inject } from "vue";


/* 
//Import if using legacy build with webpack worker load
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

if (typeof window !== "undefined" && "Worker" in window) {
  pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
    new URL("pdfjs-dist/legacy/build/pdf.worker.mjs", import.meta.url),
    { type: "module" }
  );
} */

import * as pdfjsLib from "pdfjs-dist/webpack";

const pdfjsSymbol = Symbol();

export class PDFReader {
  async setPage(pageNumber, render = true) {
    if (this.loadedDocument == null) throw 'Document needs to be loaded first.';
    if (pageNumber <= this.pageCount && pageNumber >= 1) {
      return this.loadedDocument.getPage(pageNumber).then(async (loadedPage) => {
        this.currentPage = loadedPage;
        if (render) await this.renderPage();
        return loadedPage.pageNumber;
      });
    }
    return this.getCurrentPage();
  }

  async loadPDF(pdfURL, render = true) { //TODO Implement password prompt too
    return pdfjsLib.getDocument({url: pdfURL, httpHeaders: { 'Accept': 'application/pdf' } }).promise.then((document) => { //TODO Maybe need to use some CORS headers
      this.loadedDocument = document;
      this.pageCount = document.numPages;
      return this.setPage(1, render); //Automatically start with the first page after loading
    });
  }

  setCanvas(newCanvasInstance, render = true) {
    this.canvasInstance = newCanvasInstance;
    //If no canvas is provided initialize an internal one
    if (this.canvasInstance == null) {
      //Use OffscreenCanvas if supported otherwise fallback to regular canvas element
      if (typeof OffscreenCanvas !== 'undefined') {
        this.canvasInstance = new OffscreenCanvas(48, 48);
      } else {
        this.canvasInstance = document.createElement('canvas');
      }
    }
    this.canvasContext = this.canvasInstance.getContext('2d');
    if (render && this.currentPage != null) this.renderPage();
  }

  getCanvas() {
    return this.canvasInstance;
  }

  setScales({renderingScale, canvasRatio}, render = true) {
    if (renderingScale != null) this.renderingScale = renderingScale;
    if (canvasRatio != null) this.canvasRatio = canvasRatio;

    if (render && this.currentPage != null) this.renderPage();
  }

  //Callbacks and parameters used to configure this file service
  constructor(canvasInstance, { renderingScale = 1.0, canvasRatio = 1.0 } = {}) {
    this.canvasInstance = undefined;
    this.canvasContext = undefined;
    this.currentPage = undefined;
    this.pageCount = 0;
    this.loadedDocument = undefined;
    this.renderingScale = renderingScale;
    this.canvasRatio = canvasRatio;

    //Concurrency mechanism to not render multiple pages at once
    this.currentRenderingPromise = Promise.resolve();

    this.setCanvas(canvasInstance);
  }

  getCurrentPage() {
    if (this.currentPage != null) return this.currentPage.pageNumber;
    return 0;
  }

  async renderPage() {
    //Ignore all previous errors
    this.currentRenderingPromise = this.currentRenderingPromise.catch(() => null).then(() => {
      if (this.canvasContext == null) throw 'No canvas for rendering pdf initialized';
      if (this.currentPage == null) throw 'No page loaded for rendering'
  
      let viewport = this.currentPage.getViewport({ scale: this.renderingScale });

      let deviceRatio = (window.devicePixelRatio || 1);

      let outputScale = this.canvasRatio * deviceRatio;

      let transform = (outputScale !== 1)
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null;
      
      this.canvasInstance.width = (viewport.width * outputScale);
      this.canvasInstance.height = (viewport.height * outputScale);

      return this.currentPage.render({
        canvasContext: this.canvasContext,
        transform: transform,
        viewport,
      }).promise;
    });

    return this.currentRenderingPromise;
  }

  async nextPage(render = true) {
    return this.setPage((this.getCurrentPage() + 1), render);
  }

  async previousPage(render = true) {
    return this.setPage((this.getCurrentPage() - 1), render);
  }
}

export function providePdfjs() {
  provide(pdfjsSymbol, pdfjsLib);
}

export function usePdfjs() {
  const pdfjs = inject(pdfjsSymbol);
  if (!pdfjs) throw new Error("pdfjs has to be provided first!");

  return pdfjs;
}

export default pdfjsLib;